import './TicketForm.scss';

import { useResourceParams } from '@refinedev/core';
import { DatePicker, Flex, Form, FormProps, Input, Radio } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanySelectField from '@/components/general/CompanySelectField';
import FormattedTextInput from '@/components/general/FormattedTextInput';
import InfoTooltip from '@/components/general/InfoTooltip';
import LinkTextField from '@/components/general/LinkTextField';
import PrioritySelect from '@/components/general/PrioritySelect';
import UploadedFileCarousel from '@/components/general/UploadFile/UploadedFileCarousel';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { MACHINES_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { TICKET_STATE, TicketContactTypeString } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';
import { normalizeHtmlWhitespace } from '@/utils/ensureHtml';
// Mejora: se reemplazó el onFinishFailed inline por createFinishFailedHandler genérico
// y el bloque empresa+info por CompanySelectField para eliminar duplicidad en 4 formularios.
import { createFinishFailedHandler } from '@/utils/formHelpers';

import { useTicketForm } from './useTicketForm';

interface TicketFormProps {
  formProps: FormProps;
  fileList: FileData[];
  setFileList: (files: FileData[]) => void;
  getSubmitStatus?: () => TICKET_STATE | undefined;
  onSubmitFailed?: () => void;
  setBlockTabs?: (block: boolean) => void;
}

const TicketForm = (props: TicketFormProps) => {
  const { t } = useTranslation();
  const { action } = useResourceParams();
  const { formProps, fileList, setFileList, getSubmitStatus, onSubmitFailed, setBlockTabs } = props;
  const edit = action === 'edit';
  const {
    companySelectProps,
    subCategorySelectProps,
    usersSelectProps,
    subStatusSelectProps,
    machineSelectProps,
    refTicketsSelectProps,
    formInitialValues,
    clearDraft,
  } = useTicketForm(formProps, edit, setBlockTabs);
  const machine = Form.useWatch('movil', formProps.form);
  const reference = Form.useWatch('referencia', formProps.form);

  return (
    <Form
      {...formProps}
      initialValues={formInitialValues}
      layout="vertical"
      className="TicketForm"
      onValuesChange={(changedValues, allValues) => {
        // Preserva el hook interno de refine para el warnWhenUnsavedChanges
        formProps.onValuesChange?.(changedValues, allValues);

        if (!formProps.form?.isFieldsTouched()) {
          return;
        }

        setBlockTabs?.(true);
      }}
      // Mejora: handler genérico que reemplaza el bloque onFinishFailed duplicado en 4 formularios
      onFinishFailed={createFinishFailedHandler(t, onSubmitFailed)}
      onFinish={values => {
        const submitStatus = getSubmitStatus?.();
        // Normaliza espacios no separables innecesarios antes de persistir el HTML del editor.
        const normalizedDescription = normalizeHtmlWhitespace(values.description);
        const wrappedDescription = normalizedDescription
          ? `<div id="description">${normalizedDescription}</div>`
          : undefined;

        // Wrapper a description
        formProps.onFinish?.({
          ...values,
          ...(submitStatus ? { status: submitStatus } : {}),
          description: wrappedDescription,
        });
        clearDraft?.();
      }}>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Subcategoría')}
          name={'subcategoria'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('La subcategoría es requerida'),
            },
          ]}>
          <AntdSelect {...subCategorySelectProps} allowClear showSearch />
        </Form.Item>

        <Form.Item
          label={
            <>
              {t('Título')}
              <InfoTooltip
                tooltipText={t('Se autocompletara con el nombre de la subcategoría')}
                iconClassName="TicketTitleInfoIcon"
              />
            </>
          }
          name={'title'}>
          <Input />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        {/* Mejora: CompanySelectField encapsula el patrón select + botón info repetido en 4 forms */}
        <CompanySelectField
          name="empresa"
          label={t('Empresa')}
          formProps={formProps}
          selectProps={companySelectProps}
        />
        <Form.Item label={t('Móvil')} name={'movil'}>
          <AntdSelect {...machineSelectProps} allowClear showSearch />
        </Form.Item>
      </div>
      <Form.Item label={t('Referencia')}>
        <Flex vertical gap={4}>
          <Form.Item noStyle name={'referencia'}>
            <AntdSelect {...refTicketsSelectProps} allowClear showSearch />
          </Form.Item>
          {reference && (
            <LinkTextField
              label={t('Ver ticket de referencia')}
              to={`/${RESOURCES.TICKETS}/edit/${reference}`}
              local
              newTab
            />
          )}
        </Flex>
      </Form.Item>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Asignado a')}
          name={'assigned_to'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('Se requiere asignar un responsable'),
            },
          ]}>
          <AntdSelect options={usersSelectProps.options} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Prioridad')} name={'priority'} className="TicketListFilterRow">
          <PrioritySelect />
        </Form.Item>
      </div>
      <Form.Item label={t('Subestado')} name={'substatus'} className="TicketListFilterRow">
        <AntdSelect {...subStatusSelectProps} allowClear showSearch />
      </Form.Item>

      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Tipo')}
          name={'contact_type'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Tipo') }),
            },
          ]}>
          <Radio.Group
            className="TicketFormTypeRadioGroup"
            options={Object.values(TicketContactTypeString).map((v, index) => ({
              label: t(v),
              value: index + 1,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              {t('Próximo recordatorio')}
              <InfoTooltip
                tooltipText={t('Se recibirá una notificación en la fecha seleccionada')}
              />
            </>
          }
          name={'reminder_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}>
          <DatePicker
            className="TicketFormDatePicker"
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
          />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Fecha de resolución del problema del cliente')}
          name={'finish_problem_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}>
          <DatePicker
            className="TicketFormDatePicker"
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
            onChange={() => {
              // Resolución: limpia el error manual apenas el usuario modifica la fecha.
              formProps.form?.setFields([
                {
                  name: 'finish_problem_date',
                  errors: [],
                },
              ]);
            }}
          />
        </Form.Item>
      </div>
      {/* Links adicionales */}
      {edit && formInitialValues?.movil && (
        <div style={{ marginBottom: 16 }}>
          <div>{t('Links adicionales de máquina')}</div>
          <Flex className="TicketShowDataLinks" gap={24}>
            <LinkTextField
              label={t('Admin móvil')}
              to={`${MACHINES_URLS.ADMIN_MOVIL}/${machine}`}
              admin
            />
            <LinkTextField label={t('Info de móvil')} to={`/machines/detail/${machine}`} platform />
            <LinkTextField
              label={t('Ver historial de trabajos')}
              to={`/machines/works/${machine}`}
              platform
            />
            <LinkTextField
              label={t('Ver historial por hora')}
              to={`/machines/history/${machine}`}
              platform
            />
          </Flex>
        </div>
      )}
      <Form.Item label={t('Descripción')} name={'description'}>
        <FormattedTextInput readOnly={false} />
      </Form.Item>
      <Form.Item label={t('Archivos')}>
        <UploadFileComponent fileList={fileList} setFileList={setFileList} />
      </Form.Item>
      {fileList.length > 0 && (
        <Form.Item label={t('Archivos subidos')}>
          <UploadedFileCarousel fileList={fileList} setFileList={setFileList} />
        </Form.Item>
      )}
    </Form>
  );
};

export default TicketForm;
